import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { auditLogKeys, categoryKeys } from "@/lib/query";
import type {
  AddSubtaskInput,
  CreateCategoryInput,
  UpdateCategoryInput,
  UpdateSubtaskInput,
} from "@/schemas/categories";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CategoryTag {
  jobTypeId: string;
  code: string;
  sortOrder: number;
}

export interface Subtask {
  id: string;
  categoryId: string;
  name: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  jobTypeCodes: string[];
  activeSubtaskCount: number;
  totalSubtaskCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryDetail {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  tags: CategoryTag[];
  subtasks: Subtask[];
  createdAt: string;
  updatedAt: string;
}

export interface DeactivationImpact {
  unsubmittedEntries: number;
  activeWOsLast30Days: number;
}

export interface Coverage {
  jobTypes: Array<{
    id: string;
    code: string;
    label: string;
    activeCategoryCount: number;
    isComplete: boolean;
  }>;
  incompleteCategories: Array<{
    id: string;
    name: string;
    activeSubtaskCount: number;
  }>;
}

export interface ImportPreviewResult {
  rows: Array<{
    categoryName: string;
    jobTypeCodes: string[];
    subtaskName: string;
    active: boolean;
  }>;
  errors: Array<{ row: number; field: string; message: string }>;
  rowCount: number;
}

export interface ImportSummary {
  categoriesCreated: number;
  categoriesUpdated: number;
  subtasksCreated: number;
  subtasksUpdated: number;
  bulkImportId: string;
  errors: never[];
}

// ── Queries ────────────────────────────────────────────────────────────────────

export interface ListCategoriesParams {
  jobTypeCode?: string;
  search?: string;
  includeInactive?: boolean;
}

export function useCategories(params: ListCategoriesParams = {}) {
  return useQuery({
    queryKey: categoryKeys.list(params),
    queryFn: async (): Promise<Category[]> => {
      const url = new URLSearchParams();
      if (params.jobTypeCode) url.set("jobTypeCode", params.jobTypeCode);
      if (params.search) url.set("search", params.search);
      if (params.includeInactive) url.set("includeInactive", "true");
      const res = await fetch(`/api/categories?${url}`);
      if (!res.ok) throw new Error("Failed to fetch categories");
      const json = await res.json();
      return (json as { data: Category[] }).data;
    },
  });
}

export function useCategory(id: string, enabled = true) {
  return useQuery({
    queryKey: categoryKeys.detail(id),
    queryFn: async (): Promise<CategoryDetail> => {
      const res = await fetch(`/api/categories/${id}`);
      if (!res.ok) throw new Error("Failed to fetch category");
      const json = await res.json();
      return (json as { data: CategoryDetail }).data;
    },
    enabled: enabled && !!id,
  });
}

export function useCoverage() {
  return useQuery({
    queryKey: categoryKeys.coverage(),
    queryFn: async (): Promise<Coverage> => {
      const res = await fetch("/api/categories/coverage");
      if (!res.ok) throw new Error("Failed to fetch coverage");
      const json = await res.json();
      return (json as { data: Coverage }).data;
    },
  });
}

export function useDeactivationImpact(
  entityType: "category" | "subtask",
  entityId: string,
  enabled = false,
) {
  return useQuery({
    queryKey: ["deactivation-impact", entityType, entityId],
    queryFn: async (): Promise<DeactivationImpact> => {
      const path =
        entityType === "category"
          ? `/api/categories/${entityId}/impact`
          : `/api/categories/subtasks/${entityId}/impact`;
      const res = await fetch(path);
      if (!res.ok) throw new Error("Failed to fetch impact");
      const json = await res.json();
      return (json as { data: DeactivationImpact }).data;
    },
    enabled: enabled && !!entityId,
  });
}

// ── Category mutations ─────────────────────────────────────────────────────────

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCategoryInput) => {
      const res = await fetch("/api/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error((data as { error?: string }).error ?? "Failed to create category");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      toast.success("Category created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & UpdateCategoryInput) => {
      const res = await fetch(`/api/categories/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to update category");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      toast.success("Category updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/categories/${id}`, { method: "DELETE" });
      if (res.status === 409) {
        const data = await res.json();
        throw new Error(
          `Cannot delete — ${(data as { count: number }).count} time entries reference this category`,
        );
      }
      if (!res.ok) throw new Error("Failed to delete category");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      toast.success("Category deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// ── Subtask mutations ──────────────────────────────────────────────────────────

export function useAddSubtask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ categoryId, ...data }: { categoryId: string } & AddSubtaskInput) => {
      const res = await fetch(`/api/categories/${categoryId}/subtasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to add subtask");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      toast.success("Subtask added");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateSubtask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & UpdateSubtaskInput) => {
      const res = await fetch(`/api/categories/subtasks/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to update subtask");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      toast.success("Subtask updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteSubtask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/categories/subtasks/${id}`, { method: "DELETE" });
      if (res.status === 409) {
        const data = await res.json();
        throw new Error(
          `Cannot delete — ${(data as { count: number }).count} time entries reference this subtask`,
        );
      }
      if (!res.ok) throw new Error("Failed to delete subtask");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      toast.success("Subtask deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// ── Reorder mutations ──────────────────────────────────────────────────────────

export function useReorderCategoriesInJobType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      jobTypeId,
      orderedCategoryIds,
    }: {
      jobTypeId: string;
      orderedCategoryIds: string[];
    }) => {
      const res = await fetch("/api/categories/reorder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobTypeId, orderedCategoryIds }),
      });
      if (!res.ok) throw new Error("Failed to reorder categories");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useReorderSubtasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      categoryId,
      orderedSubtaskIds,
    }: {
      categoryId: string;
      orderedSubtaskIds: string[];
    }) => {
      const res = await fetch(`/api/categories/${categoryId}/subtasks/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderedSubtaskIds }),
      });
      if (!res.ok) throw new Error("Failed to reorder subtasks");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// ── Import ─────────────────────────────────────────────────────────────────────

export function useImportCategories() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      preview = false,
    }: {
      file: File;
      preview?: boolean;
    }): Promise<ImportPreviewResult | ImportSummary> => {
      const formData = new FormData();
      formData.append("file", file);
      const url = preview ? "/api/categories/import?preview=true" : "/api/categories/import";
      const res = await fetch(url, { method: "POST", body: formData });
      if (!res.ok) {
        const data = await res.json();
        throw new Error((data as { error?: string }).error ?? "Import failed");
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      if (!variables.preview) {
        queryClient.invalidateQueries({ queryKey: categoryKeys.all });
        queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
        toast.success("Import complete");
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
